import { useEffect, useRef, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ReactTimeAgo from "react-time-ago";

import {
  LIST_REACTION_QUESTIONS,
  SOFT_DELETE_REACTION_QUESTION,
} from "../Queries/GifviewContentQueries";
import { editGifviewContent, Views } from "../store/session";
import { useAppDispatch, useAppSelector } from "../store/index";

const PAGE_SIZE = 50;

interface ReactionQuestion {
  id: string;
  title: string;
  published: boolean;
  published_date: string | null;
  created_date: string;
  created_by: string | null;
  comments_aggregate: {
    aggregate: { count: number };
    nodes: { user_id: string }[];
  };
  post_views_aggregate: {
    aggregate: { count: number };
  };
}

export function GifviewContent() {
  const dispatch = useAppDispatch();
  const view = useAppSelector((state) => state.session.view);
  const prevView = useRef(view);

  const [offset, setOffset] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const [fetchQuestions, { data, loading }] = useLazyQuery(
    LIST_REACTION_QUESTIONS,
    { fetchPolicy: "network-only" }
  );

  const [softDelete] = useMutation(SOFT_DELETE_REACTION_QUESTION);

  const loadData = () => {
    fetchQuestions({
      variables: {
        limit: PAGE_SIZE,
        offset,
        order_by: [{ created_date: "desc" }],
      },
    });
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  // Refresh when navigating back to this view
  useEffect(() => {
    if (prevView.current !== view && view === Views.GifviewContent) {
      loadData();
    }
    prevView.current = view;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const questions: ReactionQuestion[] = data?.posts ?? [];
  const total = data?.posts_aggregate?.aggregate?.count ?? 0;

  const getUniqueReactors = (q: ReactionQuestion): number => {
    const userIds = new Set(q.comments_aggregate.nodes.map((n) => n.user_id));
    return userIds.size;
  };

  const getReactionRate = (q: ReactionQuestion): string => {
    const views = q.post_views_aggregate.aggregate.count;
    const reactors = getUniqueReactors(q);
    if (views === 0) return "—";
    return `${Math.round((reactors / views) * 100)}%`;
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await softDelete({ variables: { id: deleteTarget } });
    setDeleteTarget(null);
    loadData();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5">Gifview Content</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => dispatch(editGifviewContent(undefined))}
        >
          Create Question
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Question</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Published</TableCell>
              <TableCell align="right">Reactions</TableCell>
              <TableCell align="right">Unique Reactors</TableCell>
              <TableCell align="right">Views</TableCell>
              <TableCell align="right">Reaction Rate</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            )}
            {!loading && questions.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No questions yet. Create your first one!
                </TableCell>
              </TableRow>
            )}
            {questions.map((q) => (
              <TableRow key={q.id}>
                <TableCell sx={{ maxWidth: 300 }}>
                  <Typography noWrap>{q.title}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={q.published ? "Published" : "Draft"}
                    color={q.published ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <ReactTimeAgo date={new Date(q.created_date)} />
                </TableCell>
                <TableCell>
                  {q.published_date ? (
                    <ReactTimeAgo date={new Date(q.published_date)} />
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell align="right">
                  {q.comments_aggregate.aggregate.count}
                </TableCell>
                <TableCell align="right">{getUniqueReactors(q)}</TableCell>
                <TableCell align="right">
                  {q.post_views_aggregate.aggregate.count}
                </TableCell>
                <TableCell align="right">{getReactionRate(q)}</TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => dispatch(editGifviewContent(q.id))}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteTarget(q.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {total > PAGE_SIZE && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, gap: 1 }}>
          <Button
            disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          >
            Previous
          </Button>
          <Typography sx={{ alignSelf: "center" }}>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </Typography>
          <Button
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
          >
            Next
          </Button>
        </Box>
      )}

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Question</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this question? This action can be undone by a database admin.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
